/**
 *  Centralized socket manager that uses the eventBus event aggregator
 *  to publish and subscribe to various server events.
 */
import PubSub from "pubsub-js";
import SockJS from "sockjs-client";
import Stomp from "stompjs";
import { size } from "underscore";
import config from "../../components/helpers/socket/config";
import { pubsubEvents } from "../../components/helpers/socket/pubsubEvent";
import { cmed_options_grid_messageReceived } from "../../platforms/cmed/OptionsGrid/eventEmitter";
import {
  removeCapacitorPreference,
  setCapacitorPreference,
} from "../core-utils/capPreferencesUtils";
import { BE_URL } from "../urlUtils";
import { cmed_order_book_messageReceived } from "./OrderBookEventReciever";
import { coreEventReciever } from "./coreEventReciever";
import { cmed_market_data_messageReceived } from "./marketDataEventReciever";
import { cmed_trading_service_messageRecieved } from "./tradingServiceEventReceiver";
import { CapacitorPreferencesConstants } from "../../components/Constants";
import { getCapacitorPreference } from "../core-utils/capPreferencesUtils";
import { startMetric, endMetric } from "../metric-utils/metricUtils";
import {
  MetricConstants,
  MetricTypeConstants,
} from "../../components/Constants";
import { getOrCreateStickySessionId } from "../stickySessionIdManager";
import { alertingEventReciever } from "./alertingEventReciever";
import { alerting_status_messageReceived } from "./alertingStatusReceiver";

const serviceSubscriptions: any = {};
const subscriptions: any = {};
let forcedDisconnect = false;
export let stompClient: Stomp.Client | null = null;
let asyncSocket: any = null;
let orderCancellationSubscription = null;
let firmsoftAlertsSubscription = null;
let userSpecificQueue = null;
let reconnecting = false;
/**
 * Once a STOMP client is created, it must call its connect() method to
 * effectively connect and authenticate to the STOMP server.
 *
 * @return {undefined}
 */

var recInterval: any;
export async function connect(cookie: any) {
  const stickySessionCleintId = await getOrCreateStickySessionId();
  // Check for the actual socket ready state, and its existence before creating a new socket.
  if (
    (!asyncSocket || asyncSocket?.readyState === 3) &&
    asyncSocket?.protocol !== "xhr-streaming"
  ) {
    asyncSocket = new SockJS(
      BE_URL + `/ws?x-sticky-session-id=${stickySessionCleintId}`,
    );
  } else {
    return;
  }

  stompClient = Stomp.over(asyncSocket);

  stompClient.heartbeat.incoming = 2500;
  stompClient.heartbeat.outgoing = 2500;

  // let csrfToken = $('meta[name="_csrf"]').attr('content')
  // if (!csrfToken) csrfToken = ''

  // let csrfHeader = $('meta[name="_csrf_header"]').attr('content')
  // if (!csrfHeader) csrfHeader = ''

  const headers: any = {};

  headers["csrfHeader"] = cookie;

  await stompClient.connect(
    headers,
    async (msg: any) => {
      // config.socketErrorIncrement = 0
      // On a successful connect we want to make sure and clear the last known disconnect from local storage.
      // removeDisconnectEvent()
      const user_name = msg.headers["user-name"];
      userSpecificQueue = user_name.split("|")[0];
      setCapacitorPreference(
        CapacitorPreferencesConstants.ACCESS_TOKEN,
        user_name.split("|")[1],
      );

      // invalidToken + validRefreshToken (cookie InvalidToken)
      // In the event of a successful connection resets flag
      config.socketDisconnected = false;

      stompClient!.subscribe("/queue/responses", (message) => {
        let data = JSON.parse(message.body),
          payload = data.payload;
        console.log("Data", data);

        if (
          data.status &&
          (data.status === "ERROR" || data.status === "FAILURE")
        ) {
          return PubSub.publish(
            pubsubEvents.socketKeys.HANDLE_SOCKET_ERROR,
            data,
          );
        }
      });

      stompClient?.subscribe(
        config.socket.subscriptions.ALERT_STATUS,
        (message) => {
          alerting_status_messageReceived(JSON.parse(message.body));
        },
      );

      reconnecting &&
        PubSub.publish(pubsubEvents.socketKeys.ATTEMPT_RECONNECT, false);
      reconnecting &&
        PubSub.publish(
          pubsubEvents.successKeys.NETWORK_UP_TOAST,
          "Back Online",
        );
      reconnecting = false;
      //TODO: HANDLE THIS LATER FOR CONNECTION UPDATE
      // stompClient.subscribe("/queue/connection", (message) => {
      //   console.log("connection:update", JSON.parse(message.body));
      //   const data = JSON.parse(message.body);

      //   if (
      //     data.status &&
      //     (data.status === "ERROR" || data.status === "FAILURE")
      //   ) {
      //     switch (data.type) {
      //       case "chat/connection":
      //         PubSub.publish(
      //           pubsubEvents.connectionKeys.CHAT_DISCONNECT,
      //           JSON.parse(message.body)
      //         );
      //         break;
      //       default:
      //         break;
      //     }
      //   }
      // });

      // This triggers the start of the app
      PubSub.publish(pubsubEvents.socketKeys.CONNECTED_WEBSOCKET);
      console.log("Published CONNECTED_WEBSOCKET");
      clearInterval(recInterval);

      forcedDisconnect = false;
    },
    async (err: any) => {
      if (err?.command?.includes("ERROR")) {
        if (err?.headers?.message?.includes("302")) {
          console.log("Invalid Token -> Attempting to refresh", err);
          await removeCapacitorPreference("accessToken");
          PubSub.publish(pubsubEvents.socketKeys.ATTEMPT_VISIBILITY_RECONNECT);
          return;
        }
        if (forcedDisconnect) return;

        /**
         * Handles a disconnection/failure to connect by reattempting to
         * connect 3 times, then logging user out.  A reconnect can also be
         * fired from our visibilityDetection.
         */
      }
      console.log("Closing socket");
      config.socketDisconnected = true;
      reconnecting === false &&
        PubSub.publish(pubsubEvents.socketKeys.ATTEMPT_RECONNECT);
      reconnecting = true;
      startMetric(
        MetricConstants.TIME_TO_FIRST_PAINT_RECONNECTING_NETWORK,
        MetricTypeConstants.TIMER,
      );
      if (forcedDisconnect !== true) {
        recInterval = setInterval(async function () {
          const value = await getCapacitorPreference(
            CapacitorPreferencesConstants.ACCESS_TOKEN,
          );
          connect(value);
        }, 3500);
      }
    },
  );
}

/**
 * [disconnect description]
 * @return {[type]} [description]
 */
export function disconnect() {
  forcedDisconnect = true;
  asyncSocket.close();
  asyncSocket = null;
}

/**
 * Makes a send request via the stompClient.
 * @param  {String} channel Request channel.
 * @param  {Object} request Request object.
 * @return {undefined}
 */
export function send(channel: any, request: any) {
  if (config.socketDisconnected || !stompClient?.connected) {
    return;
  }

  stompClient.send(channel, {}, JSON.stringify(request));
}

/**
 * Proxy send method that uses a wrapped timeout making sure
 * the socket is ready before processing the send request
 * to use the actual socket send method.
 *
 * @param  {string}   message  JSON string data to be sent to server.
 * @param  {function} callback Optional callback function.
 */
export function reqRepSend(message: any, callback: any) {
  send(config.socket.channels.CORE, message);
  if (typeof callback !== "undefined") {
    callback();
  }
}

export function unsubscribeAll() {
  if (size(subscriptions)) {
    Object.keys(subscriptions).forEach((key, index) => {
      console.log(`async.unsubscribing.all id: ${subscriptions[key].id}`);
      stompClient?.unsubscribe(subscriptions[key].id);
      subscriptions[key] = null;
      delete subscriptions[key];
    });
  }
}

export function subscribeToServiceQueue(service: any) {
  serviceSubscriptions[`/user/queue/${service}`] = stompClient!.subscribe(
    `/user/queue/${service}`,
    (message) => {
      // if (!config.socketDebugDisabled) {
      console.log("------------------------------------");
      console.log(`✈️✈️✈️ /user/queue/${service}`, JSON.parse(message.body));
      const parsedBody = JSON.parse(message.body);
      // tradingMessageRecieved(JSON.parse(message.body))
      if (parsedBody.service === "core") {
        coreEventReciever(parsedBody);
      } else {
        cmed_trading_service_messageRecieved(parsedBody);
        cmed_order_book_messageReceived(parsedBody);
        cmed_market_data_messageReceived(parsedBody);
        cmed_options_grid_messageReceived(parsedBody);
        alertingEventReciever(parsedBody);
      }
    },
  );
}

export function unsubscribeFromServiceQueue(service: any) {
  console.log("BEFORE::", serviceSubscriptions);
  stompClient!.unsubscribe(serviceSubscriptions[`/user/queue/${service}`].id);
  delete serviceSubscriptions[`/user/queue/${service}`];
  console.log("AFTER::", serviceSubscriptions);
}

export function subscribeToService(service: any) {
  // App specific instance health messages i.e. services
  serviceSubscriptions[`${service}-topic`] = stompClient?.subscribe(
    `/topic/app/${service}/events/`,
    (message) => {
      const data = JSON.parse(message.body);

      if (data.action && data.action === config.socket.actions.SERVICE_STATUS) {
        return PubSub.publish(
          pubsubEvents.socketKeys.PLATFORM_SERVICE_OUTAGE,
          data,
        );
      }

      return false;
    },
  );

  // User specific instance health messages i.e. instances
  serviceSubscriptions[`${service}-queue`] = stompClient?.subscribe(
    `/user/queue/app/${service}/events/`,
    (message) => {
      const data = JSON.parse(message.body);

      if (data.action && data.action === config.socket.actions.SERVICE_STATUS) {
        return PubSub.publish(
          pubsubEvents.socketKeys.PLATFORM_SERVICE_OUTAGE,
          data,
        );
      }

      return false;
    },
  );
}

export function unsubscribeFromService(service: any, _callback: any) {
  if (
    size(serviceSubscriptions[`${service}-topic`]) &&
    size(serviceSubscriptions[`${service}-queue`])
  ) {
    stompClient?.unsubscribe(serviceSubscriptions[`${service}-topic`].id);
    stompClient?.unsubscribe(serviceSubscriptions[`${service}-queue`].id);
    delete serviceSubscriptions[`${service}-topic`];
    delete serviceSubscriptions[`${service}-queue`];
  }

  return _callback();
}

export function unsubscribeFromAllServices() {
  if (size(serviceSubscriptions)) {
    Object.keys(serviceSubscriptions).forEach((key, index) => {
      console.log(
        `async.unsubscribing.service id: ${serviceSubscriptions[key].id}`,
      );
      stompClient?.unsubscribe(serviceSubscriptions[key].id);
      serviceSubscriptions[key] = null;
      delete serviceSubscriptions[key];
    });
  }
}
/** ******************
 * PLATFORM CONTENT *
 ******************* */

export function contentSubscribe(data: any) {
  if (config.socketDisconnected) {
    return;
  }

  stompClient?.subscribe(data.subscriptionURL, (message) => {
    const parsedMessage = JSON.parse(message.body);
    if (typeof data.subscriptionCallback === "function") {
      data.subscriptionCallback(parsedMessage);
    }
  });
}

export function contentUnsubscribe(data: any) {
  if (config.socketDisconnected) {
    return;
  }
  stompClient?.unsubscribe(data.subscriptionId);
}
